import qs, { IStringifyOptions } from 'qs';

export interface FetcherOptions<
  TQueryParams = never,
  TBody = never,
  THeaderParams = HeadersInit
> extends Omit<RequestInit, 'body' | 'headers'> {
  url: string;
  queryParams?: TQueryParams extends never ? undefined : TQueryParams;
  queryParamsStringifyOptions?: IStringifyOptions;
  body?: TBody extends never ? undefined : TBody;
  headers?: THeaderParams;
  unAuthTries?: number;
}

/**
 * Interface should able to do following:
 * 1. Intercept options and modify it
 * 2. Intercept response and perform some action
 * 3. Catch all errors and perform some action
 * 4. Retry on error
 */

export enum FetcherInterceptor {
  PRE_PROCESS_OPTIONS = 'PRE_PROCESS_OPTIONS',
  POST_PROCESS_RESPONSE = 'POST_PROCESS_RESPONSE',
  ON_ERROR = 'ON_ERROR'
}

interface FetcherInterceptorOptions {
  options: FetcherOptions;
  response?: Response;
  retryFn?: (options: FetcherOptions) => Promise<Response>;
  error?: unknown;
}

const interceptFunctions:
  | {
      [Key in FetcherInterceptor]: (
        arg: FetcherInterceptorOptions
      ) => FetcherOptions | Promise<Response | undefined> | void;
    }
  | Object = {};

export const registerInterceptor = (
  interceptor: FetcherInterceptor,
  fn: (
    arg: FetcherInterceptorOptions
  ) => FetcherOptions | Promise<Response | undefined> | void
) => {
  if (interceptFunctions[interceptor]) {
    console.warn(`Interceptor ${interceptor} reinitialized!`);
  }
  interceptFunctions[interceptor] = fn;
};

const JSON_HEADERS = ['application/json'];

export async function fetcher<
  TResponse = unknown,
  TQueryParams = never,
  TBody = never,
  THeaderParams = HeadersInit
>(
  options: FetcherOptions<TQueryParams, TBody, THeaderParams>
): Promise<TResponse> {
  const {
    body,
    url,
    queryParams,
    queryParamsStringifyOptions,
    headers,
    ...rest
  } = interceptFunctions[FetcherInterceptor.PRE_PROCESS_OPTIONS]
    ? interceptFunctions[FetcherInterceptor.PRE_PROCESS_OPTIONS]({ options })
    : options;

  let finalUrl = `${url}`;
  if (queryParams && Object.keys(queryParams).length) {
    finalUrl += `?${qs.stringify(queryParams, {
      arrayFormat: 'comma',
      ...queryParamsStringifyOptions
    })}`;
  }
  return new Promise(async (resolve, reject) => {
    fetch(finalUrl, {
      body: body ? JSON.stringify(body) : undefined,
      headers: {
        'Content-Type': JSON_HEADERS[0],
        ...(headers as unknown as HeadersInit)
      },
      ...rest
    })
      .then(resp => {
        if (interceptFunctions[FetcherInterceptor.POST_PROCESS_RESPONSE]) {
          return interceptFunctions[FetcherInterceptor.POST_PROCESS_RESPONSE]({
            options,
            response: resp,
            retryFn: (opt: FetcherOptions) => fetcher(opt)
          });
        }
        return resp;
      })
      .then((resp: Response) => {
        /**
          We use POST_PROCESS_RESPONSE to handle errors and one of the cases is to call the retryFn which will call this function again
          This means that when the second time this function is called and the call is successful, the response will be a resolved object 
          and we cannot call .json() or .text() on it. In this case, we just return the response as is
          This is hacky and we should avoid recursion in the first place
        */
        if (resp && !resp.headers && !resp.json && !resp.text) {
          return resolve(resp as unknown as TResponse);
        }

        const contentType = resp?.headers.get('Content-Type');
        const asJson =
          contentType && JSON_HEADERS.some(h => contentType.startsWith(h));

        resolve(asJson ? resp?.json() : resp?.text());
      })
      .catch(error => {
        if (interceptFunctions[FetcherInterceptor.ON_ERROR]) {
          interceptFunctions[FetcherInterceptor.ON_ERROR]({ error, options });
        }
        reject(error);
      });
  });
}
